<script setup lang="ts">
import type { InputNumberInputEvent, InputNumberPassThroughOptions } from 'primevue/inputnumber';
import {
  computed,
  toRef,
} from 'vue';

import VFormFloatLabelInputWrapper
  from '@modules/forms/components/inputs/VFormFloatLabelInputWrapper/VFormFloatLabelInputWrapper.vue';
import VFormInputWrapper
  from '@modules/forms/components/inputs/VFormInputWrapper/VFormInputWrapper.vue';
import { useI18nStore } from '@modules/i18n/store';
import { isNumber } from '@utils/guards';

import type {
  VFormNumberInputEmits,
  VFormNumberInputProps,
} from './types';
import { useInputInnerValue } from '../composables';

const props = withDefaults(defineProps<VFormNumberInputProps>(), {
  maxFractionDigits: 2,
  format: true,
  useGrouping: true,
  readonlyStyle: true,
});

const emit = defineEmits<VFormNumberInputEmits>();

const currentLocale = toRef(useI18nStore(), 'currentLocale');

const inputNumberLocale = computed(() => currentLocale.value.replace('_', '-'));

const inputNumberPt = computed<InputNumberPassThroughOptions>(() => ({
  pcInputText: {
    root: {
      class: {
        'p-2 w-16 min-w-0': props.isSmall,
        'min-w-0': !props.isSmall,
        'input-readonly': props.readonlyStyle && props.readonly,
      },
      autocomplete: props.autocomplete ? props.autocomplete : 'off',
    },
  },
}));

const { innerValue } = useInputInnerValue(toRef(props, 'value'));

function onInput(event: InputNumberInputEvent): void {
  if (isNumber(event.value)) {
    innerValue.value = event.value;
  } else {
    innerValue.value = undefined;
  }

  emit('input', innerValue.value);
  emit('change', innerValue.value);
}
</script>

<template>
  <VFormInputWrapper v-data-qa-auto-id="'VFormNumberInput'"
    :errors="errors"
    :class="isFullWidth ? 'w-full' : 'input-max-width'"
    :hide-errors="hideErrors"
    :description="description"
    :description-severity="descriptionSeverity"
    :readonly="readonly"
  >
    <VFormFloatLabelInputWrapper
      :name="name"
      :label="label"
      :icon-left="iconLeft"
      :icon-right="iconRight"
      :not-float-label="notFloatLabel"
      :required="required"
      :readonly="readonly"
    >
      <PInputNumber
        v-model="innerValue"
        :name="name"
        :input-id="name"
        :placeholder="placeholder"
        :required="required"
        :readonly="readonly"
        :disabled="disabled"
        :max-fraction-digits="maxFractionDigits"
        :min-fraction-digits="minFractionDigits"
        :locale="inputNumberLocale"
        :use-grouping="useGrouping"
        :show-buttons="!readonly && !disabled && !disableButtons"
        :min="min"
        :max="max"
        :format="format"
        class="w-full"
        :class="{ 'p-invalid': isInvalid && !readonly }"
        :pt="inputNumberPt"
        @blur="emit('blur', innerValue)"
        @focus="emit('focus', innerValue)"
        @input="onInput"
      />
    </VFormFloatLabelInputWrapper>
  </VFormInputWrapper>
</template>
